import { useCallback } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import type { AppDispatch } from '@/store'
import { announcementApi } from '@/api/announcementApi'
import { fetchPendingAnnouncements, removeAnnouncement } from '@/store/slices/announcementsSlice'
import { publishAnnouncementEvent } from './announcementBroadcast'
import type { SnoozeKind } from './types'
import type { SnowflakeId } from '@/types/snowflake'

// websocket-first：去掉周期轮询后，snooze 到点重弹靠本地定时器（后端 /snooze 返回 snoozedUntil）。
// 模块级保存定时器：再次 snooze 同一公告时清理旧的、避免重复触发。长 snooze（>~26h）不排，
// 靠 focus/visibility 对账兜（浏览器 setTimeout 跨很长时间 / 标签关闭后不可靠）。
const SNOOZE_TIMERS = new Map<string, ReturnType<typeof setTimeout>>()
const MAX_SNOOZE_TIMER_MS = 26 * 60 * 60 * 1000
function scheduleSnoozeRepop(id: SnowflakeId, snoozedUntil: string | undefined, dispatch: AppDispatch) {
  const key = String(id)
  const existing = SNOOZE_TIMERS.get(key)
  if (existing) { clearTimeout(existing); SNOOZE_TIMERS.delete(key) }
  if (!snoozedUntil) return
  const delay = new Date(snoozedUntil).getTime() - Date.now()
  if (!(delay > 0) || delay > MAX_SNOOZE_TIMER_MS) return
  const timer = setTimeout(() => {
    SNOOZE_TIMERS.delete(key)
    void dispatch(fetchPendingAnnouncements())
    publishAnnouncementEvent({ kind: 'refetch' })
  }, delay)
  SNOOZE_TIMERS.set(key, timer)
}

/**
 * Read / snooze actions. Both optimistically drop the item from the local pending
 * list (server /pending won't return it again); on failure we refetch to resync.
 */
export function useAnnouncementActions() {
  const { t } = useTranslation('announcements')
  const dispatch = useDispatch<AppDispatch>()

  const markRead = useCallback(async (id: SnowflakeId) => {
    dispatch(removeAnnouncement(id))
    publishAnnouncementEvent({ kind: 'refetch' })
    try {
      await announcementApi.markRead(id)
    } catch {
      toast.error(t('toastReadFailed'))
      void dispatch(fetchPendingAnnouncements())
    }
  }, [dispatch, t])

  const snooze = useCallback(async (id: SnowflakeId, kind: SnoozeKind) => {
    dispatch(removeAnnouncement(id))
    publishAnnouncementEvent({ kind: 'refetch' })
    try {
      const res = await announcementApi.snooze(id, kind)
      scheduleSnoozeRepop(id, res?.snoozedUntil, dispatch) // 到点本地重弹(替代周期轮询撞上)
    } catch {
      toast.error(t('toastSnoozeFailed'))
      void dispatch(fetchPendingAnnouncements())
    }
  }, [dispatch, t])

  // Mandatory acknowledge (scenario 17). On failure refetch (the blocking dialog
  // should re-surface) so the user can retry — never silently drop a forced ack.
  const acknowledge = useCallback(async (id: SnowflakeId) => {
    try {
      await announcementApi.acknowledge(id)
      dispatch(removeAnnouncement(id))
      publishAnnouncementEvent({ kind: 'refetch' })
    } catch {
      toast.error(t('toastAckFailed'))
      void dispatch(fetchPendingAnnouncements())
    }
  }, [dispatch, t])

  return { markRead, snooze, acknowledge }
}
